import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { GraphQLTaggedNode, useMutation, UseMutationConfig } from "react-relay"
import { MutationParameters, PayloadError } from "relay-runtime"
import { toast } from "@/hooks/useToast"
import { extractGraphqlErrors } from "@/lib/utils"
import { ConfirmationPropsWithoutOpen } from "@/stores/useConfirmStore"

interface CustomMutationParams {
  confirmProps?: ConfirmationPropsWithoutOpen
  errorMessage?: string
}

// The purpose of this custom mutation hook is to execute the mutation with enhanced error handling
function useMutationWithErrorToast<T extends MutationParameters>(
  mutation: GraphQLTaggedNode,
  params?: CustomMutationParams,
): [(config: UseMutationConfig<T>) => void, boolean] {
  const [commit, isInFlight] = useMutation<T>(mutation)
  const { t } = useTranslation()

  const { shouldShowErrorToast = true, errorMessage } = { ...params }

  const showErrorToast = useCallback(() => {
    toast({
      description: t(errorMessage ?? "error.500.description"),
    })
  }, [errorMessage, t])

  const customCommit = useCallback(
    (config: UseMutationConfig<T>) => {
      const configWithHandling = {
        ...config,
        onCompleted: (response: T["response"], graphqlErrors: PayloadError[] | null) => {
          if (graphqlErrors?.length) {
            if (shouldShowErrorToast) showErrorToast()

            // Call original onError if exists
            config.onError?.(new Error(graphqlErrors[0].message))
            return
          }

          const errors = extractGraphqlErrors(response)
          if (errors.length > 0) {
            if (shouldShowErrorToast) showErrorToast()

            // Call original onError if exists
            config.onError?.(new Error(errors[0]?.message))
            return
          }

          // Call original onCompleted if exists
          config.onCompleted?.(response, graphqlErrors)
        },
        onError: (error: Error) => {
          if (shouldShowErrorToast) showErrorToast()

          // Call original onError if exists
          config.onError?.(error)
        },
      }

      commit(configWithHandling)
    },
    [commit, shouldShowErrorToast, showErrorToast],
  )

  return [customCommit, isInFlight]
}

export { useMutationWithErrorToast, type CustomMutationParams }
